/**
 * AnuraIntegrationPage — Anura fraud-detection partner integration config.
 * Form: API key + fraud-score threshold + recent sync status.
 * POSTs a test fraud event via useAnuraIntegrationCreate.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useAnuraIntegrationCreate, useAnuraIntegrationList } from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

const VERDICT_OPTIONS = ["good", "suspect", "bad"] as const;
type Verdict = (typeof VERDICT_OPTIONS)[number];

function validateThreshold(raw: string): { ok: boolean; value: number; error: string } {
	if (raw.trim() === "") return { ok: false, value: 0, error: "threshold required" };
	const n = Number(raw);
	if (!Number.isFinite(n)) return { ok: false, value: 0, error: "threshold must be a number" };
	if (n < 0 || n > 100) return { ok: false, value: 0, error: "threshold must be 0 to 100" };
	return { ok: true, value: Math.round(n), error: "" };
}

export function AnuraIntegrationPage() {
	const createMut = useAnuraIntegrationCreate();
	const listQ = useAnuraIntegrationList();

	const [apiKey, setApiKey] = useState("");
	const [threshold, setThreshold] = useState("50");
	const [eventId, setEventId] = useState("");
	const [ip, setIp] = useState("");
	const [verdict, setVerdict] = useState<Verdict>("good");
	const [score, setScore] = useState("0");
	const [saved, setSaved] = useState(false);
	const [localErr, setLocalErr] = useState("");

	const thresholdCheck = useMemo(() => validateThreshold(threshold), [threshold]);
	const scoreCheck = useMemo(() => validateThreshold(score), [score]);

	const items = listQ.data?.items ?? [];
	const lastItem = items[0] as
		| { verdict?: string; score?: number; created_at?: number; event_id?: string }
		| undefined;
	const syncLabel = listQ.isLoading
		? "Checking."
		: lastItem
			? `Last sync: ${lastItem.verdict ?? "-"} at score ${lastItem.score ?? 0}`
			: "No sync yet";

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		setLocalErr("");
		if (!apiKey.trim()) {
			setLocalErr("api key required");
			return;
		}
		if (!thresholdCheck.ok) {
			setLocalErr(thresholdCheck.error);
			return;
		}
		if (!eventId.trim()) {
			setLocalErr("event_id required");
			return;
		}
		if (!ip.trim()) {
			setLocalErr("ip required");
			return;
		}
		if (!scoreCheck.ok) {
			setLocalErr("score must be 0 to 100");
			return;
		}
		createMut.mutate(
			{
				event_id: eventId.trim(),
				ip: ip.trim(),
				verdict,
				score: scoreCheck.value,
				user_agent: "dash-config",
			},
			{
				onSuccess: () => {
					setSaved(true);
					setEventId("");
				},
			},
		);
	};

	return (
		<main
			data-testid="anura-integration-page"
			className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold text-green-700">
					Anura Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Configure Anura fraud-detection API key, score threshold, and send a test event.
				</p>
				<p
					className="text-sm text-zinc-700"
					role="status"
					data-testid="anura-integration-sync-status"
				>
					{syncLabel}
				</p>
			</header>

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="anura-integration-form"
				aria-label="anura-integration form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="anura-api-key" className={LABEL}>
						Anura API Key
					</label>
					<input
						id="anura-api-key"
						type="password"
						required
						className={INPUT}
						value={apiKey}
						onChange={(e) => setApiKey(e.currentTarget.value)}
						data-testid="anura-integration-apikey-input"
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="anura-threshold" className={LABEL}>
						Fraud Score Threshold (0-100)
					</label>
					<input
						id="anura-threshold"
						type="number"
						min={0}
						max={100}
						required
						className={INPUT}
						value={threshold}
						onChange={(e) => setThreshold(e.currentTarget.value)}
						data-testid="anura-integration-threshold-input"
					/>
					{!thresholdCheck.ok && threshold !== "" ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="anura-integration-threshold-error"
						>
							{thresholdCheck.error}
						</p>
					) : null}
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="anura-event-id" className={LABEL}>
							Test event_id
						</label>
						<input
							id="anura-event-id"
							type="text"
							className={INPUT}
							value={eventId}
							onChange={(e) => setEventId(e.currentTarget.value)}
							data-testid="anura-integration-event-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="anura-ip" className={LABEL}>
							IP
						</label>
						<input
							id="anura-ip"
							type="text"
							className={INPUT}
							value={ip}
							onChange={(e) => setIp(e.currentTarget.value)}
							data-testid="anura-integration-ip-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="anura-verdict" className={LABEL}>
							Verdict
						</label>
						<select
							id="anura-verdict"
							className={INPUT}
							value={verdict}
							onChange={(e) => setVerdict(e.currentTarget.value as Verdict)}
							data-testid="anura-integration-verdict-input"
						>
							{VERDICT_OPTIONS.map((v) => (
								<option key={v} value={v}>
									{v}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="anura-score" className={LABEL}>
							Score (0-100)
						</label>
						<input
							id="anura-score"
							type="number"
							min={0}
							max={100}
							className={INPUT}
							value={score}
							onChange={(e) => setScore(e.currentTarget.value)}
							data-testid="anura-integration-score-input"
						/>
					</div>
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending}
						data-testid="anura-integration-save"
					>
						{createMut.isPending ? "Sending." : "Send test event"}
					</button>
					{saved ? (
						<p
							className="text-sm text-green-700"
							role="status"
							data-testid="anura-integration-saved"
						>
							Saved.
						</p>
					) : null}
					{localErr ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="anura-integration-local-error"
						>
							{localErr}
						</p>
					) : null}
					{createMut.error && !localErr ? (
						<p className="text-sm text-red-600" role="alert">
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>
		</main>
	);
}

export default AnuraIntegrationPage;
